import { find, isEmpty, isNil, sortBy, uniqBy } from 'lodash';
import { AllocationScenarioDetailSecurityModel } from 'api';
import { ConfigurationSpreadsheets } from 'common/types/scalarSpreadsheet';
import { AllocationWeightedShareValue, OwnershipEntity, ShareValueSecurity } from 'common/types/valuation';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import {
  ALLOCATION_WEIGHTED_SHARE_VALUES_KEY,
  SHEET_CONFIG_CONSTANTS,
  SHEET_TITLES_CONSTANTS,
} from 'pages/ValuationsAllocation/common/constants/weightedShareValues';
import { Configuration } from 'pages/ValuationsAllocation/hooks';
import { calculateAveragePricePerSecurity } from 'pages/ValuationsAllocation/util';
import { getArrayValue, getNumberOrNAValue, getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { createColumns } from './createColumns';
import { customParser, WeightedShareValuesParserParams } from './customParser';
import { customReverseParser, ReverseParserParams } from './customReverseParser';
import customRowConfig from './customRowConfig/customRowConfig';
import { CreateWeightedShareValuesConfigurationParams, FundWeightedShareValues, SecuritiesByFund } from './types';
import { RelatedEntity } from '../../../types';

const { ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_TABLE_TERMS } = SHEET_CONFIG_CONSTANTS;
const { ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SHARES_TITLE } = SHEET_TITLES_CONSTANTS;

const createWeightedShareValuesConfiguration = (
  params: CreateWeightedShareValuesConfigurationParams
): Configuration[] => {
  const { capTable, fieldAttributes, funds, isUniformCurrency, securities, valuation, companyInfo, isChildCompany }
    = params;

  const { allocation } = getObjectValue(valuation);
  if (!allocation) {
    throw new Error('Allocation is required to create Weighted Share Values Configuration');
  }
  const { allocation_scenarios: allocationScenarios = [], weighted_share_values: weightedShareValues = [] }
    = getObjectValue(allocation);

  const sortedAllocationScenarios = sortBy(allocationScenarios, 'order');

  const { fund_ownership: capTableFundOwnership } = getObjectValue(capTable);
  const { fund_ownership_detail: capTableFundOwnershipDetail = [] } = getObjectValue(capTableFundOwnership);

  const ownershipEntityMapping = new Map<number, OwnershipEntity>();

  capTableFundOwnershipDetail.forEach(({ fund, related_entity }) => {
    if (fund) {
      const fundInformation = funds?.find(f => f.id === fund);
      if (fundInformation) {
        ownershipEntityMapping.set(fund, { id: fund, name: fundInformation.name ?? '', fund: fundInformation });
      }
    }

    if (related_entity) {
      const relatedEntityInformation = companyInfo.child_companies?.find(
        f => Number(f.company_id) === related_entity
      ) as RelatedEntity;
      if (relatedEntityInformation) {
        ownershipEntityMapping.set(related_entity, {
          id: related_entity,
          name: relatedEntityInformation.company_name,
          relatedEntity: relatedEntityInformation,
        });
      }
    }
  });

  const securitiesAveragePrice
    = securities.length > 0 && allocationScenarios.length > 0
      ? calculateAveragePricePerSecurity(securities, allocationScenarios)
      : null;

  // If WSV haven't been created, calculate them
  const calculatedWeightedShareValues = Array.from(ownershipEntityMapping.values()).map(ownershipEntity => {
    // ownershipEntity could be either a fund or a holding company
    const entityId = ownershipEntity.id;
    const wsv = weightedShareValues?.find(wsv => wsv.fund?.id === entityId || wsv.related_entity?.id === entityId);
    const currentOwnershipEntityDetails = capTableFundOwnershipDetail.filter(
      fundOwnership =>
        fundOwnership.entity_reference === companyInfo.id
        && (fundOwnership.fund === entityId || fundOwnership.related_entity === entityId)
    );
    // check if wsv has all the fund details
    if (wsv?.share_values?.length && wsv.share_values.length === currentOwnershipEntityDetails.length) {
      const { entity, fund, ...rest } = wsv;
      return {
        ...rest,
        ...ownershipEntity,
      };
    }

    const wsvs
      = currentOwnershipEntityDetails.map(fundOwnership => {
        const {
          security: securityId,
          security_name: securityName,
          shares,
          custom_security_name: customSecurityName,
          loan_value: loanValue,
        } = fundOwnership;
        const securityPrice = securityId && securitiesAveragePrice ? securitiesAveragePrice[securityId] : 0;
        const securityShares = Number(shares ?? 0);
        const price = Number(securityPrice ?? 0);
        return {
          securityId: securityId ?? undefined,
          security: securityName ?? customSecurityName,
          shares: securityShares,
          price,
          unrealized_value: securityShares * price,
          xfactor: 0,
          is_custom_security: false,
          invested_capital: 0,
          loan_value: getNumberValue(loanValue),
        };
      }) || [];

    return {
      ...ownershipEntity,
      name: `${ownershipEntity.name} ${ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SHARES_TITLE}`,
      share_values: wsvs,
    };
  });

  const allocationWeightedShareValuesByEntity = getObjectValue(
    calculatedWeightedShareValues?.reduce((accumulator, current) => {
      const { share_values: shareValues = [] } = getObjectValue(current);
      const { id: entityId = 0 } = getObjectValue(current);

      return {
        ...accumulator,
        [entityId]: shareValues,
      };
    }, {} as FundWeightedShareValues)
  );

  const securitiesByEntity = getObjectValue(
    (capTableFundOwnershipDetail ?? [])
      .filter(({ entity_reference }) => entity_reference === companyInfo.id)
      .reduce((accumulator, current) => {
        const {
          custom_security_name: customSecurityName,
          fund: fundId,
          related_entity: relatedEntityId,
          invested_capital: investedCapital,
          security_name: securityName,
          security: securityId,
          loan_value: loanValue,
          shares,
        } = getObjectValue(current);

        const entityId = fundId ?? relatedEntityId;

        if (!entityId) {
          return accumulator;
        }

        const fundWeightedShareValues = getObjectValue(allocationWeightedShareValuesByEntity?.[entityId]);

        const fundWeightedShareValuesSecurity = find(fundWeightedShareValues, {
          // customSecurityName could be an empty string so we can't use the ?? operator:
          security: customSecurityName || securityName,
        });

        const customSecurity = {
          invested_capital: getNumberValue(investedCapital),
          is_custom_security: isNil(securityId),
          name: getStringValue(isNil(securityId) ? customSecurityName : securityName),
          price: getNumberValue(fundWeightedShareValuesSecurity?.price), // Custom Securities do not have a Price
          security: getStringValue(isNil(securityId) ? customSecurityName : securityName),
          securityId: isNil(securityId) ? undefined : securityId, // Custom Securities do not have a SecurityId
          shares: getNumberValue(shares ?? fundWeightedShareValuesSecurity?.shares),
          unrealized_value: getNumberValue(fundWeightedShareValuesSecurity?.unrealized_value),
          xfactor: getNumberOrNAValue(fundWeightedShareValuesSecurity?.xfactor),
          loan_value: getNumberValue(fundWeightedShareValuesSecurity?.loan_value),
        } as ShareValueSecurity;

        return {
          ...accumulator,
          [entityId]: [...getArrayValue(accumulator?.[entityId]), customSecurity],
        };
      }, {} as SecuritiesByFund)
  );

  const weightedShareValuesByEntity = Array.from(ownershipEntityMapping.values()).map(entity => {
    const { id: entityId = 0, name: entityName } = getObjectValue(entity);

    const fundShareValues = getArrayValue(securitiesByEntity?.[entityId]);

    const fundSecurities = getArrayValue(
      allocationWeightedShareValuesByEntity?.[entityId]?.reduce((accumulator, current) => {
        const { security: securityName } = getObjectValue(current);

        const fundSecurity = find(fundShareValues, { security: securityName, is_custom_security: false });

        const fundSecuritiesFromShareValues = fundShareValues?.filter(security => security?.security === securityName);

        const totalShares = fundSecuritiesFromShareValues?.reduce(
          (acc, curr) => acc + getNumberValue(curr?.shares),
          0
        );

        const totalInvestedCapital = fundSecuritiesFromShareValues?.reduce(
          (acc, curr) => acc + getNumberValue(curr?.invested_capital),
          0
        );

        const updatedSecurity = {
          ...current, // Allocation Security
          ...fundSecurity,
          shares: totalShares,
          invested_capital: totalInvestedCapital,
          name: getStringValue(securityName),
        } as ShareValueSecurity;

        return [...accumulator, updatedSecurity];
      }, [] as ShareValueSecurity[])
    );

    const fundCustomSecurities = getArrayValue(fundShareValues?.filter(shareValue => shareValue?.is_custom_security));

    return {
      entity,
      name: `${entityName} ${ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_SHARES_TITLE}`,
      share_values: [...fundSecurities, ...fundCustomSecurities].map(security => ({
        ...security,
        entity,
      })),
    } as AllocationWeightedShareValue;
  });

  const filteredWeightedShareValuesByEntity = sortBy(
    weightedShareValuesByEntity.filter(weightedShareValues => !isEmpty(weightedShareValues?.share_values)),
    'name'
  );

  // when generating the config if it is for a child company then declare that
  const spreadsheetConfig = (isChildCompanyWSV: boolean) => {
    // for child companies we need to add the child company name to the config
    const { name } = companyInfo;

    const spreadsheetTableTerms = ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_TABLE_TERMS(`${companyInfo.id}`);

    const rowConfig = customRowConfig(
      name,
      capTable.fund_ownership!,
      funds,
      capTable.securities ?? [],
      (companyInfo.child_companies as RelatedEntity[]) ?? []
    );
    const shareValues = filteredWeightedShareValuesByEntity.map(i => i.share_values || []).flat();
    const columns = createColumns({ shareValues });

    const parser = (parserParams: WeightedShareValuesParserParams) =>
      customParser({
        ...parserParams,
        allocationScenarios: sortedAllocationScenarios,
        isUniformCurrency,
        relatedEntityId: isChildCompanyWSV ? `${companyInfo.id}` : '',
      });

    const currentSecurities = getArrayValue(
      uniqBy([...getArrayValue(securities), ...getArrayValue(shareValues)], 'name')
    ) as AllocationScenarioDetailSecurityModel[];

    const reverseParser = (reverseParserParams: ReverseParserParams) =>
      customReverseParser({
        ...reverseParserParams,
        funds: funds ?? [],
        securities: currentSecurities,
        weightedShareValues: calculatedWeightedShareValues,
        allocation,
      });

    const sheet = new SpreadsheetConfig({
      allowConfirmAndDeleteColumn: false,
      allowCopyColumn: false,
      allowReorderColumns: false as unknown as SpreadsheetConfig['allowReorderColumns'],
      alwaysDisplayLegend: false,
      columns,
      currencyFormatter: true,
      fieldAttributes,
      format: undefined,
      name: spreadsheetTableTerms.tableName,
      page: ALLOCATION_WEIGHTED_SHARE_VALUES_KEY as unknown as SpreadsheetConfig['page'],
      parser,
      reverseParser: reverseParser as unknown as SpreadsheetConfig['reverseParser'],
      rowConfig,
      showPreviousColsDivider: false,
      showTitlesColumn: true,
      showToolbar: true,
      showTotalColumn: false,
      tableData: {},
      tableTerms: spreadsheetTableTerms as unknown as SpreadsheetConfig['tableTerms'],
      totalParser: undefined,
      unitsFormatter: true,
    }) as unknown as ConfigurationSpreadsheets;

    return [
      {
        isWeightedShareValues: true,
        name: spreadsheetTableTerms.tableName,
        spreadsheets: {
          [spreadsheetTableTerms.tableName]: sheet,
        },
      } as Configuration,
    ];
  };

  return spreadsheetConfig(isChildCompany ?? false);
};

export default createWeightedShareValuesConfiguration;
